import { useState } from 'react'
import dayjs from 'dayjs'
import startCase from 'lodash/startCase'

import EyeOutlinedIcon from '@ant-design/icons/EyeOutlined'

import Button from '@mui/material/Button'
import Popover from '@mui/material/Popover'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import IconButton from '@components/_mui/IconButton'
import Typography from '@components/_mui/Typography'

import usePromiseLoading from '@shared/hooks/src/usePromiseLoading'
import { isUserAllowed, useMe } from '@shared/providers/src/MeProvider'
import { handleError, InsurancePartners, UserRole } from '@shared/utils'

import PatientAccessGuard, { usePatientAccessible } from '@components/PatientAccessGuard'
import Property from '@components/Property'

import { useSendInsurancePartnerInvite, useSetInsurancePartnerContacted, useSetInsurancePartnerInsured } from './InsurancePartner.hooks'

export default function InsurancePartner({ insurance, patient, isLoading }) {
  const patientId = patient?.id

  const sendInvite = useSendInsurancePartnerInvite(patientId)
  const setContacted = useSetInsurancePartnerContacted(patientId)
  const setInsured = useSetInsurancePartnerInsured(patientId)

  const handleSetContacted = () => setContacted.mutateAsync(patientId).catch(handleError)
  const handleSetInsured = () => setInsured.mutateAsync(patientId).catch(handleError)
  const sendPartnerInvite = () => sendInvite.mutateAsync(patientId).catch(handleError)

  if (isLoading || !patient)
    return (
      <Typography sx={{ color: 'text.secondary' }}>
        <Skeleton />
      </Typography>
    )

  if (!insurance) return null
  if (!insurance.cboPartneredWithPrideLife && !insurance.cboPartneredWithAmericanExchange) return null

  const currentPartner = insurance.cboInsurancePartners?.find((partner) => partner.current)
  const previousPartner = insurance.cboInsurancePartners?.find((partner) => !partner.current)
  const oneYearAgo = dayjs().subtract(1, 'year')
  const canResendInvite = currentPartner?.referredDate && dayjs(currentPartner.referredDate).isBefore(oneYearAgo)

  return (
    <>
      {currentPartner ? (
        <Partner
          patientId={patientId}
          label="Current Partnership"
          data={currentPartner}
          onSendInvite={sendPartnerInvite}
          canResendInvite={canResendInvite}
          onSetContacted={handleSetContacted}
          onSetInsured={handleSetInsured}
        />
      ) : (
        <Partner
          patientId={patientId}
          label="Current Partnership"
          data={{ partner: insurance.cboPartneredWithPrideLife ? InsurancePartners.PrideLife : InsurancePartners.AmericanExchange }}
          onSendInvite={sendPartnerInvite}
        />
      )}
      {previousPartner && !currentPartner && (
        <Partner
          patientId={patientId}
          label="Previous Partnership"
          data={previousPartner}
          onSetContacted={handleSetContacted}
          onSetInsured={handleSetInsured}
        />
      )}
    </>
  )
}

function Partner({ patientId, label, data, onSendInvite, canResendInvite, onSetContacted, onSetInsured }) {
  const [anchorEl, setAnchorEl] = useState(null)

  const handleViewDetails = (event) => setAnchorEl(event.currentTarget)
  const handleClose = () => setAnchorEl(null)

  const me = useMe()
  const [handleSetContacted, loadingContracted] = usePromiseLoading(onSetContacted)
  const [handleSetInsured, loadingInsured] = usePromiseLoading(onSetInsured)

  const open = Boolean(anchorEl)
  const isCurrentPartner = data.current
  const patientAccessible = usePatientAccessible(patientId)
  const canUpdate = isUserAllowed(me, undefined, [
    UserRole.Manager,
    UserRole.Manager340B,
    UserRole.Compliance,
    UserRole.ClinicalLHC,
    UserRole.ClinicalQI,
  ])

  return (
    <>
      <Property
        label={label}
        value={
          <Stack
            direction="row"
            sx={{
              width: '100%',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 0.5,
            }}
          >
            <Typography>{startCase(data.partner)}</Typography>
            {canResendInvite ? (
              <>
                <Typography sx={{ color: 'text.secondary' }}>{dayjs(data.referredDate).format('L')}</Typography>
                <IconButton onClick={handleViewDetails}>
                  <EyeOutlinedIcon />
                </IconButton>
                {canUpdate && (
                  <PatientAccessGuard patientId={patientId}>
                    <Button variant="contained" size="small" onClick={onSendInvite}>
                      Resend
                    </Button>
                  </PatientAccessGuard>
                )}
              </>
            ) : data.referredDate ? (
              <>
                <Typography sx={{ color: 'text.secondary' }}>{dayjs(data.referredDate).format('L')}</Typography>
                <IconButton onClick={handleViewDetails}>
                  <EyeOutlinedIcon />
                </IconButton>
              </>
            ) : (
              onSendInvite &&
              canUpdate && (
                <PatientAccessGuard patientId={patientId}>
                  <Button variant="contained" size="small" onClick={onSendInvite}>
                    Send Invitation
                  </Button>
                </PatientAccessGuard>
              )
            )}
          </Stack>
        }
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ '& .MuiPaper-root': { width: 300, p: 2 } }}
      >
        <Stack spacing={2}>
          <Typography variant="h5">{startCase(data.partner)}</Typography>

          <Stack spacing={2}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ color: 'text.secondary' }}>Referred</Typography>
              <Typography>{data.referredDate ? dayjs(data.referredDate).format('L') : 'n/a'}</Typography>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ color: 'text.secondary' }}>Contacted</Typography>
              {data.contactedDate ? (
                <Typography>{dayjs(data.contactedDate).format('L')}</Typography>
              ) : canUpdate && isCurrentPartner && patientAccessible ? (
                <Button variant="contained" size="small" onClick={handleSetContacted} loading={loadingContracted} disabled={loadingInsured}>
                  Set Date
                </Button>
              ) : (
                <Typography>-</Typography>
              )}
            </Stack>

            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ color: 'text.secondary' }}>Insured</Typography>
              {data.insuredDate ? (
                <Typography>{dayjs(data.insuredDate).format('L')}</Typography>
              ) : canUpdate && isCurrentPartner && patientAccessible ? (
                <Button variant="contained" size="small" onClick={handleSetInsured} loading={loadingInsured} disabled={loadingContracted}>
                  Set Date
                </Button>
              ) : (
                <Typography>-</Typography>
              )}
            </Stack>
          </Stack>

          <Button fullWidth variant="outlined" size="small" onClick={handleClose}>
            Close
          </Button>
        </Stack>
      </Popover>
    </>
  )
}
